import type { z } from 'zod';
import {
    FormCheckboxField,
    FormCheckboxGroupField,
    FormCounterField,
    FormHiddenInput,
    FormInputField,
    FormSelectionCardGroupField,
    FormSwitchField,
    FormTextareaField,
    GetEnumPrefix,
} from '@mews/framework';
import {
    DateField,
    DateTimeField,
    DropzoneField,
    EnumSelectField,
    NumberInputField,
    SelectField,
    TimeField,
} from '../../fields';
import { Compound } from '../compound';
import type { SupportedSchema } from '../types';
import type { Unwrap } from '../unwrap';
import type { ComponentProps } from './types';

/**
 * Components added for convenience, but for which we can't infer additional props from the schema
 */
export const simpleComponentMap = {
    checkboxGroup: FormCheckboxGroupField,
    counter: FormCounterField,
    date: DateField,
    dateTime: DateTimeField,
    file: DropzoneField,
    hidden: FormHiddenInput,
    number: NumberInputField,
    switch: FormSwitchField,
    text: FormInputField,
    textarea: FormTextareaField,
    time: TimeField,
} as const;
type SimpleComponentMap = typeof simpleComponentMap;

declare namespace PredefinedFields {
    export type CardSelect = ComponentProps<'cardSelect', typeof FormSelectionCardGroupField, 'type'>;

    export type Checkbox = ComponentProps<'checkbox', typeof FormCheckboxField, 'children'>;

    export type Compound<FieldSchema extends z.ZodTypeAny> = ComponentProps<
        'compound',
        typeof Compound<Unwrap<FieldSchema> extends SupportedSchema ? Unwrap<FieldSchema> : never>,
        'schema'
    >;

    export type Enum<FieldSchema extends z.ZodTypeAny> = ComponentProps<
        'enum',
        typeof EnumSelectField<GetEnumPrefix<FieldSchema['_input']>>,
        'enumClass' | 'multiselect'
    >;

    export type Select = ComponentProps<'select', typeof SelectField, 'multiselect'>;

    export type SimpleFields = {
        [K in keyof SimpleComponentMap]: ComponentProps<K, SimpleComponentMap[K]>;
    }[keyof SimpleComponentMap];
}

export type PredefinedField<FieldSchema extends z.ZodTypeAny> =
    | PredefinedFields.CardSelect
    | PredefinedFields.Checkbox
    | PredefinedFields.Compound<FieldSchema>
    | PredefinedFields.Enum<FieldSchema>
    | PredefinedFields.Select
    | PredefinedFields.SimpleFields;
